import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { setHandbookContentHeight } from "../../../app/appSlice";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { faEraser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useAuthSessionQuery,
  useUpdateActivityMutation,
} from "../../../app/zetaApi";
import { useActivity } from "../../../services/useActivity";
import { useDatabase } from "../../../db/useDatabase";
import { onValue, set } from "firebase/database";
import { getActivityUrl } from "../../../app/configuration";
import { useConfirmationModal } from "../../confirmation-modal/useConfirmationModal";
import { faCheckCircle } from "@fortawesome/free-regular-svg-icons";

type Props = {
  title: string;
  subtitle: string;
  page?: number;
  totalPages?: number;
  goBack?: () => void;
  chaptersCrumbs?: { color: string; name: string }[];
  color?: string;
  isFacilitator?: boolean;
  resetProgress?: any;
};

const Header = ({
  title,
  subtitle,
  color,
  isFacilitator,
  resetProgress,
}: Props) => {
  const dispatch = useAppDispatch();
  const headerRef = useRef<any>();
  const [updateActivity] = useUpdateActivityMutation();
  const activity = useActivity();
  const { confirm } = useConfirmationModal();
  const authSession = useAuthSessionQuery();

  const statusDb = useDatabase("status");

  useEffect(() => {
    if (!statusDb) {
      return;
    }

    return onValue(statusDb, (snap) => {
      const status = snap.val();
      if (!activity.data || !authSession.data) {
        return;
      }
      if (status === null) {
        set(statusDb, activity.data.status);
        return;
      }
      if (
        status === "completed" &&
        (!isFacilitator || activity.data.status !== "completed")
      ) {
        window.location.href = getActivityUrl(
          activity.data,
          authSession.data.organization
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusDb]);

  const completeActivity = async () => {
    if (!activity.data) return;

    try {
      const res = await updateActivity({
        id: activity.data.id,
        status: "completed",
      }).unwrap();

      set(statusDb, res.status);
    } catch (e) {}
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      const boundingRect = headerRef.current?.getBoundingClientRect();
      boundingRect &&
        dispatch(
          setHandbookContentHeight(
            Math.floor(
              window.innerHeight - (boundingRect.y + boundingRect.height)
            )
          )
        );
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerRef]);

  return (
    <div
      ref={headerRef}
      className="justify-content-between d-flex gap-4 sidebar-header p-4 flex-center"
    >
      <div className="fs-4 fw-bolder d-flex gap-4 flex-center">
        {activity.data?.resource.spec.icon?.url && (
          <div
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: "#d4d4d4",
              borderRadius: "4px",
              backgroundImage: `url(${activity.data?.resource.spec.icon?.url})`,
              backgroundSize: "cover",
              flexShrink: 0,
            }}
          />
        )}
        <span>{activity.data?.resource.title}</span>
      </div>
      {isFacilitator && (
        <div className="d-flex gap-2">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip className="tooltip-dark"> Mark as Completed</Tooltip>
            }
          >
            <div
              className="sidebar-header-menu"
              onClick={() => {
                confirm(
                  "Course.ConfirmationModal.Message.MarkAsCompleted"
                ).then((val) => {
                  if (val) {
                    completeActivity();
                  }
                });
              }}
            >
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip className="tooltip-dark">Reset All Progress</Tooltip>
            }
          >
            <div className="sidebar-header-menu" onClick={resetProgress}>
              <FontAwesomeIcon icon={faEraser} />
            </div>
          </OverlayTrigger>
        </div>
      )}
    </div>
  );
};

export default Header;
